import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { ArrowRight, CheckCircle2, TrendingUp } from 'lucide-react';
import { servicesSeedData } from '../../database/seedData/services';
import EngagementTriggerMap from '../../components/EngagementTriggerMap';
import ExecutiveSummary from '../../components/ExecutiveSummary';
import {
  ExecutiveFlowRail,
  ExecutiveSplitSection,
  PortfolioFragmentationDiagram,
} from '../../components/executive';

const aiEngagementLanes = (useCases) => [
  {
    label: 'Portfolio & Spend',
    description: 'When AI activity outpaces enterprise visibility or alignment.',
    icon: 'wallet',
    triggers: [useCases[0], useCases[2], useCases[5]].filter(Boolean),
  },
  {
    label: 'Risk & Regulation',
    description: 'When failure, scrutiny, or new rules force a reset.',
    icon: 'shield',
    triggers: [useCases[1], useCases[3]].filter(Boolean),
  },
  {
    label: 'Architecture & Lock-in',
    description: 'When model, cloud, or vendor choices carry long consequences.',
    icon: 'layers',
    triggers: [useCases[4]].filter(Boolean),
  },
];

const AIAdvisory = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const service = servicesSeedData.find(s => s.slug === 'ai-advisory');

  if (!service) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-24`}>
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className={`text-5xl font-bold mb-8 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Service Not Found</h1>
          <button
            onClick={() => navigate('/services')}
            className={`px-10 py-4 rounded-xl text-xl font-semibold ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}
          >
            View All Services
          </button>
        </div>
      </div>
    );
  }

  const phases = service.timeline
    ? [
        { title: 'Assess', description: service.timeline.discovery },
        { title: 'Weigh Options', description: service.timeline.strategy || service.timeline.design },
        { title: 'Pressure-Test', description: service.timeline.implementation || service.timeline.production },
      ].filter((entry) => entry.description)
    : [];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
      
      {/* Hero - Classic Executive Style with Side Accent */}
      <div className={`relative ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'} overflow-hidden`}>
        {/* Gold accent bar - luxury touch */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-amber-400 via-yellow-500 to-amber-600"></div>

        <div className="max-w-7xl mx-auto px-8 py-24 lg:py-32">
          <div className="flex justify-between items-center mb-12">
            <button
              onClick={() => navigate('/services')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold bg-white/10 text-white border border-white/30 hover:bg-white/20 transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Services
            </button>
            <span className="text-sm uppercase tracking-[0.4em] text-amber-400/80">
              Shared Oxygen Advisory
            </span>
          </div>
          <div className="max-w-4xl">
            {/* Eyebrow text */}
            <div className="flex items-center mb-6">
              <div className="h-px w-12 bg-amber-500 mr-4"></div>
              <span className="text-amber-400 uppercase tracking-widest text-sm font-semibold">
                Advisory
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              {service.title}
            </h1>
            
            <p className="text-2xl lg:text-3xl text-gray-200 font-normal leading-relaxed mb-12">
              {service.shortDescription}
            </p>
            
            {/* Stats removed - no fabricated data */}

            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center px-12 py-5 bg-amber-500 hover:bg-amber-600 text-gray-900 text-lg font-bold rounded-none transition-all duration-300"
            >
              Request a Conversation
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className={`${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-20`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSummary theme={theme} variant="amber">
            {service.description}
          </ExecutiveSummary>
        </div>
      </div>

      {/* Context for leadership */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSplitSection
            theme={theme}
            accentBar="bg-amber-500"
            title="The Leadership Challenge"
            illustration={<PortfolioFragmentationDiagram theme={theme} />}
          >
            <p>
              Most organizations now have AI activity in every major function—often funded separately, governed
              inconsistently, and measured on incompatible metrics. Leadership is asked to approve further investment
              without a consolidated view of what already exists, what it costs, or what could go wrong.
            </p>
            <p>
              We provide independent counsel at the executive level—on portfolio discipline, governance,
              vendor and architecture choices, and the trade-offs that come with each. We do not resell platforms
              or embed for implementation. Our role is to strengthen how leadership thinks about AI, not to
              prescribe a fixed package of work.
            </p>
          </ExecutiveSplitSection>
        </div>
      </div>

      {/* Advisory focus */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            
            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-amber-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Where We Advise
                </h3>
                <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  Questions we help leadership work through—not a catalog of predetermined outputs.
                </p>
              </div>
              <div className="space-y-6">
                {service.keyFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start group">
                    <CheckCircle2 className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-amber-400' : 'text-amber-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-emerald-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  What Shifts
                </h3>
                <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  How counsel changes the quality of executive judgment—not a promise of specific results.
                </p>
              </div>
              <div className="space-y-6">
                {service.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start group">
                    <TrendingUp className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* When counsel is sought */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-14">
            <div className="h-1 w-20 bg-amber-500 mb-6"></div>
            <h2 className={`text-4xl lg:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              When Organizations Seek Counsel
            </h2>
            <p className={`mt-4 text-lg max-w-3xl ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
              Situations where an independent perspective helps before capital, contracts, or organizational
              commitments harden.
            </p>
          </div>

          <EngagementTriggerMap
            lanes={aiEngagementLanes(service.useCases)}
            theme={theme}
            variant="amber"
            hubLabel="Where judgment is needed"
          />
        </div>
      </div>

      {/* Approach */}
      {phases.length > 0 && (
        <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/40' : 'bg-gray-50'}`}>
          <div className="max-w-6xl mx-auto px-8">
            <div className="text-center mb-12">
              <div className="h-1 w-16 bg-amber-500 mx-auto mb-6"></div>
              <h2 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                How We Work
              </h2>
              <p className={`mt-4 text-lg max-w-3xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                A natural sequence for advisory—not a fixed engagement model. Depth and pace follow the
                decisions in front of you and how your organization already governs major investments.
              </p>
            </div>
            <ExecutiveFlowRail phases={phases} theme={theme} variant="amber" />
          </div>
        </div>
      )}

      {/* CTA - Bold, executive style */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'}`}>
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Discuss Your AI Priorities
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            An initial conversation to understand your situation, constraints, and what kind of counsel would be useful.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button 
              onClick={() => navigate('/contact')} 
              className="px-16 py-6 bg-amber-500 hover:bg-amber-600 text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Contact Us
            </button>
            <button 
              onClick={() => navigate('/services')} 
              className="px-16 py-6 border-2 border-white text-white hover:bg-white hover:text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Explore All Services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisory;